/**
 * @fileoverview Repair Cost Section Component
 * @module RepairCostSection
 * 
 * Business Context:
 * - Unified component for documenting repair costs in maintenance checklists
 * - Allows users to EITHER upload vendor invoices OR provide an estimate
 * - Invoices are the primary/preferred path (stronger evidence)
 * - Estimates serve as backup when invoices are not available
 * - Data integrates with the evidence packet for the assessor meeting
 * 
 * User Journey:
 * 1. User sees toggle: "Do you have invoices/quotes for these repairs?"
 * 2. If YES → Upload invoice(s) with amounts and scope
 * 3. If NO → Select from estimate ranges or enter custom amount
 */

import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  FileText, 
  Plus, 
  Info, 
  CheckCircle2,
  Receipt,
  Calculator,
  HelpCircle
} from 'lucide-react';
import { VendorInvoiceDialog, VendorInvoiceList } from '../evidence';

type CostDocumentationType = 'invoice' | 'estimate' | null;

interface VendorInvoice {
  id: string;
  fileName: string;
  amount: number;
  description: string;
  fileSize: number;
  uploadedAt: string;
}

interface RepairCostSectionProps {
  category: string;
  findingId: string | undefined;
  invoices: VendorInvoice[];
  estimatedCost: number;
  onEstimatedCostChange: (value: number) => void;
  onInvoiceChange: () => void;
  hasIssues: boolean; // Only show if user has checked some issues
}

// Cost ranges by category (in cents)
const costRanges: Record<string, { min: number; max: number; label: string; description: string }[]> = {
  roof: [
    { min: 0, max: 50000, label: '$0 - $500', description: 'Minor repairs (patching, sealant)' },
    { min: 50000, max: 200000, label: '$500 - $2,000', description: 'Moderate repairs (section of shingles)' },
    { min: 200000, max: 500000, label: '$2,000 - $5,000', description: 'Significant repairs (multiple areas)' },
    { min: 500000, max: 1000000, label: '$5,000 - $10,000', description: 'Major repairs (large sections)' },
    { min: 1000000, max: 2000000, label: '$10,000 - $20,000', description: 'Partial replacement' },
    { min: 2000000, max: 5000000, label: '$20,000 - $50,000', description: 'Full roof replacement' },
  ],
  foundation: [
    { min: 0, max: 100000, label: '$0 - $1,000', description: 'Minor crack sealing' },
    { min: 100000, max: 500000, label: '$1,000 - $5,000', description: 'Crack repair & waterproofing' },
    { min: 500000, max: 1500000, label: '$5,000 - $15,000', description: 'Stabilization (piers, underpinning)' },
    { min: 1500000, max: 3000000, label: '$15,000 - $30,000', description: 'Major structural repair' },
    { min: 3000000, max: 10000000, label: '$30,000+', description: 'Extensive foundation work' },
  ],
  hvac: [
    { min: 0, max: 50000, label: '$0 - $500', description: 'Maintenance & tune-up' },
    { min: 50000, max: 150000, label: '$500 - $1,500', description: 'Minor repairs (parts, refrigerant)' },
    { min: 150000, max: 400000, label: '$1,500 - $4,000', description: 'Component replacement (compressor, etc.)' },
    { min: 400000, max: 800000, label: '$4,000 - $8,000', description: 'Major repairs (ductwork, unit)' },
    { min: 800000, max: 1500000, label: '$8,000 - $15,000', description: 'Full system replacement' },
  ],
  plumbing: [
    { min: 0, max: 30000, label: '$0 - $300', description: 'Minor repairs (leaks, fixtures)' },
    { min: 30000, max: 100000, label: '$300 - $1,000', description: 'Fixture replacement' },
    { min: 100000, max: 300000, label: '$1,000 - $3,000', description: 'Pipe repairs' },
    { min: 300000, max: 800000, label: '$3,000 - $8,000', description: 'Major repairs (sewer line, water heater)' },
    { min: 800000, max: 2000000, label: '$8,000 - $20,000', description: 'Full repiping' },
  ],
  electrical: [
    { min: 0, max: 50000, label: '$0 - $500', description: 'Minor repairs (outlets, switches)' },
    { min: 50000, max: 200000, label: '$500 - $2,000', description: 'Circuit repairs' },
    { min: 200000, max: 500000, label: '$2,000 - $5,000', description: 'Panel upgrade' },
    { min: 500000, max: 1000000, label: '$5,000 - $10,000', description: 'Major repairs' },
    { min: 1000000, max: 2000000, label: '$10,000 - $20,000', description: 'Full rewiring' },
  ],
};

const RepairCostSection: React.FC<RepairCostSectionProps> = ({
  category,
  findingId,
  invoices,
  estimatedCost,
  onEstimatedCostChange,
  onInvoiceChange,
  hasIssues
}) => {
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  
  // Determine documentation type based on existing data
  const [documentationType, setDocumentationType] = useState<CostDocumentationType>(() => {
    if (invoices.length > 0) return 'invoice';
    if (estimatedCost > 0) return 'estimate';
    return null;
  });

  // Update documentation type when invoices change
  useEffect(() => {
    if (invoices.length > 0 && documentationType !== 'invoice') {
      setDocumentationType('invoice');
    }
  }, [invoices.length]);

  const ranges = costRanges[category.toLowerCase()] || costRanges.roof;
  const selectedRange = ranges.find(r => estimatedCost >= r.min && estimatedCost < r.max);
  
  const formatCurrency = (cents: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  // Calculate total from invoices
  const totalInvoiceAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);

  const handleInvoiceUpload = () => {
    setShowInvoiceDialog(false);
    onInvoiceChange();
  };

  // If no issues are checked, don't show this section
  if (!hasIssues) {
    return null;
  }

  // If no documentation type selected yet, show the choice
  if (documentationType === null) {
    return (
      <div className="mt-8 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Document Repair Costs</h3>
            <p className="text-sm text-gray-600">Strengthen your case with cost documentation</p>
          </div>
        </div>

        <p className="text-gray-700 mb-6">
          Do you have repair quotes or invoices from contractors for these issues?
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Invoice Option - Primary/Preferred */}
          <button
            onClick={() => {
              setDocumentationType('invoice');
              if (findingId) {
                setShowInvoiceDialog(true);
              }
            }}
            className="p-5 bg-white rounded-xl border-2 border-green-300 hover:border-green-500 hover:shadow-md transition-all text-left group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                <Receipt className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900">Yes, I have invoices</span>
                  <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                    Recommended
                  </span>
                </div>
                <p className="text-sm text-gray-500">Upload quotes or invoices</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Contractor quotes and invoices provide the strongest evidence of repair costs for your appeal.
            </p>
          </button>

          {/* Estimate Option - Backup */}
          <button
            onClick={() => setDocumentationType('estimate')}
            className="p-5 bg-white rounded-xl border-2 border-gray-200 hover:border-teal-300 hover:shadow-md transition-all text-left group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                <Calculator className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <span className="font-bold text-gray-900">No, provide an estimate</span>
                <p className="text-sm text-gray-500">Select a cost range</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Don't have invoices yet? Provide your best estimate based on typical repair costs.
            </p>
          </button>
        </div>

        {/* Help text */}
        <div className="mt-4 flex items-start gap-2 text-sm text-gray-500">
          <HelpCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>
            You can always come back and add invoices later once you get quotes from contractors. 
            Estimates are useful for now, but invoices carry more weight in your appeal.
          </p>
        </div>

        {/* Invoice Dialog */}
        {findingId && (
          <VendorInvoiceDialog
            isOpen={showInvoiceDialog}
            onClose={() => setShowInvoiceDialog(false)}
            findingId={findingId}
            onUploadComplete={handleInvoiceUpload}
            categoryName={`${category.toLowerCase()} repairs`}
          />
        )}
      </div>
    );
  }

  // Show Invoice Section
  if (documentationType === 'invoice') {
    return (
      <div className="mt-8 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Receipt className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Repair Invoices & Quotes</h3>
              <p className="text-sm text-gray-600">Upload contractor documentation</p>
            </div>
          </div>
          {findingId && (
            <button
              onClick={() => setShowInvoiceDialog(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Invoice
            </button>
          )}
        </div>

        {!findingId && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Save your checklist first, then you'll be able to upload invoices.
              </p>
            </div>
          </div>
        )}

        {invoices.length > 0 ? (
          <>
            <VendorInvoiceList
              invoices={invoices}
              onDelete={onInvoiceChange}
            />
            
            {/* Total Summary */}
            <div className="mt-4 p-4 bg-white rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-gray-900">Total Documented</span>
                </div>
                <span className="text-xl font-bold text-green-600">
                  {formatCurrency(totalInvoiceAmount)}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="p-6 bg-white rounded-lg border-2 border-dashed border-gray-300 text-center">
            <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium mb-1">No invoices uploaded yet</p>
            <p className="text-gray-500 text-sm mb-4">
              Upload repair quotes or invoices from contractors
            </p>
            {findingId && (
              <button
                onClick={() => setShowInvoiceDialog(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Upload Invoice
              </button>
            )}
          </div>
        )}

        {/* Switch to Estimate */}
        <div className="mt-4 pt-4 border-t border-green-200">
          <button
            onClick={() => setDocumentationType('estimate')}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <Calculator className="w-4 h-4" />
            Don't have invoices? Provide an estimate instead
          </button>
        </div>

        {/* Invoice Dialog */}
        {findingId && (
          <VendorInvoiceDialog
            isOpen={showInvoiceDialog}
            onClose={() => setShowInvoiceDialog(false)}
            findingId={findingId}
            onUploadComplete={handleInvoiceUpload}
            categoryName={`${category.toLowerCase()} repairs`}
          />
        )}
      </div>
    );
  }

  // Show Estimate Section
  return (
    <div className="mt-8 p-6 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl border-2 border-teal-200">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
          <Calculator className="w-5 h-5 text-teal-600" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900">Estimated Repair Cost</h3>
          <p className="text-sm text-gray-600">Select the range that best fits your situation</p>
        </div>
      </div>

      {/* Cost Range Buttons */}
      <div className="grid md:grid-cols-2 gap-3 mb-4">
        {ranges.map((range, index) => (
          <button
            key={index}
            onClick={() => onEstimatedCostChange(range.min + Math.floor((range.max - range.min) / 2))}
            className={`p-4 text-left rounded-lg border-2 transition-all ${
              selectedRange === range
                ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200'
                : 'border-gray-200 bg-white hover:border-teal-300'
            }`}
          >
            <div className="font-semibold text-gray-900">{range.label}</div>
            <div className="text-sm text-gray-500 mt-1">{range.description}</div>
          </button>
        ))}
      </div>

      {/* Custom Amount Input */}
      <div className="p-4 bg-white rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Or enter a specific amount:
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
          <input
            type="number"
            value={estimatedCost > 0 ? Math.round(estimatedCost / 100) : ''}
            onChange={(e) => onEstimatedCostChange(parseInt(e.target.value || '0') * 100)}
            placeholder="Enter amount"
            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>
      </div>

      {/* Selected Value Display */}
      {estimatedCost > 0 && (
        <div className="mt-4 p-4 bg-white rounded-lg border border-teal-200">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-gray-900 font-medium">
                Estimated repair cost: <span className="text-teal-600 font-bold">{formatCurrency(estimatedCost)}</span>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                This estimate helps build your case. For stronger evidence, consider getting actual quotes from contractors.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Switch to Invoice */}
      <div className="mt-4 pt-4 border-t border-teal-200">
        <button
          onClick={() => {
            setDocumentationType('invoice');
            // Clear estimate when switching to invoice
            onEstimatedCostChange(0);
          }}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <Receipt className="w-4 h-4" />
          Have invoices? Upload them for stronger evidence
        </button>
      </div>
    </div>
  );
};

export default RepairCostSection;
